import React, { useState } from 'react';
import { format } from 'date-fns';
import { payrollService } from '../../../lib/checkInAttendanceService';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PayrollProcessing = ({
  payrollRecords,
  hrProfiles,
  attendanceData,
  loading,
  onRefresh,
  gymId,
  managerId
}) => {
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedRecord, setSelectedRecord] = useState(null);

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'bg-success/10 text-success';
      case 'paid':
        return 'bg-primary/10 text-primary';
      case 'pending':
        return 'bg-warning/10 text-warning';
      case 'disputed':
        return 'bg-error/10 text-error';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return '$0.00';
    return `$${parseFloat(amount)?.toFixed(2)}`;
  };

  const formatHours = (hours) => {
    if (!hours) return '0.00';
    return parseFloat(hours)?.toFixed(2);
  };

  const handleApprove = async (recordId) => {
    const { error } = await payrollService?.approvePayroll(recordId, managerId);
    if (!error) {
      onRefresh?.();
    }
  };

  const filteredRecords = payrollRecords?.filter((record) => {
    return filterStatus === 'all' || record?.status === filterStatus;
  });

  const totalPending = payrollRecords?.filter((r) => r?.status === 'pending')?.length || 0;
  const totalGrossPay = payrollRecords?.reduce((sum, r) => sum + (parseFloat(r?.gross_pay) || 0), 0);
  const totalNetPay = payrollRecords?.reduce((sum, r) => sum + (parseFloat(r?.net_pay) || 0), 0);

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-lg bg-warning/10">
              <Icon name="Clock" size={24} className="text-warning" />
            </div>
            <div>
              <div className="text-2xl font-bold text-foreground">{totalPending}</div>
              <div className="text-xs text-muted-foreground">Pending Approval</div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-lg bg-primary/10">
              <Icon name="Users" size={24} className="text-primary" />
            </div>
            <div>
              <div className="text-2xl font-bold text-foreground">{hrProfiles?.length || 0}</div>
              <div className="text-xs text-muted-foreground">Total Employees</div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-lg bg-success/10">
              <Icon name="DollarSign" size={24} className="text-success" />
            </div>
            <div>
              <div className="text-2xl font-bold text-foreground">{formatCurrency(totalGrossPay)}</div>
              <div className="text-xs text-muted-foreground">Total Gross Pay</div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-lg bg-primary/10">
              <Icon name="TrendingUp" size={24} className="text-primary" />
            </div>
            <div>
              <div className="text-2xl font-bold text-foreground">{formatCurrency(totalNetPay)}</div>
              <div className="text-xs text-muted-foreground">Total Net Pay</div>
            </div>
          </div>
        </div>
      </div>
      {/* Filters */}
      <div className="card">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h2 className="text-xl font-semibold text-foreground">Payroll Records</h2>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e?.target?.value)}
              className="px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="paid">Paid</option>
              <option value="disputed">Disputed</option>
            </select>
          </div>
          <Button onClick={onRefresh} variant="outline" disabled={loading}>
            <Icon name={loading ? 'Loader2' : 'RefreshCw'} size={18} className={loading ? 'animate-spin' : ''} />
          </Button>
        </div>
      </div>
      {/* Payroll Records */}
      {loading && payrollRecords?.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <Icon name="Loader2" size={32} className="animate-spin text-primary" />
        </div>
      ) : filteredRecords?.length === 0 ? (
        <div className="card text-center py-12">
          <Icon name="DollarSign" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">No Payroll Records</h3>
          <p className="text-muted-foreground">Payroll records will appear here</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredRecords?.map((record) => (
            <div
              key={record?.id}
              className="card hover:shadow-lg transition-shadow cursor-pointer"
              onClick={() => setSelectedRecord(record)}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <Icon name="User" size={24} className="text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">
                      {record?.staff?.full_name}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {format(new Date(record?.pay_period_start), 'MMM d')} -{' '}
                      {format(new Date(record?.pay_period_end), 'MMM d, yyyy')}
                    </p>
                    <span className={`inline-block mt-1 px-2 py-0.5 text-xs font-medium rounded-full ${getStatusColor(record?.status)}`}>
                      {record?.status}
                    </span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-primary">{formatCurrency(record?.net_pay)}</div>
                  <div className="text-xs text-muted-foreground">Net Pay</div>
                </div>
              </div>

              <div className="grid grid-cols-4 gap-4 p-4 bg-muted/30 rounded-lg">
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Regular Hours</div>
                  <div className="font-semibold text-foreground">{formatHours(record?.regular_hours)}h</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Overtime Hours</div>
                  <div className="font-semibold text-warning">{formatHours(record?.overtime_hours)}h</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Gross Pay</div>
                  <div className="font-semibold text-foreground">{formatCurrency(record?.gross_pay)}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Deductions</div>
                  <div className="font-semibold text-error">
                    {formatCurrency(
                      (parseFloat(record?.tax_deductions) || 0) +
                        (parseFloat(record?.benefit_deductions) || 0) +
                        (parseFloat(record?.other_deductions) || 0)
                    )}
                  </div>
                </div>
              </div>

              {record?.status === 'pending' && (
                <div className="mt-4 flex items-center gap-2">
                  <Button
                    onClick={(e) => {
                      e?.stopPropagation();
                      handleApprove(record?.id);
                    }}
                    size="sm"
                  >
                    <Icon name="Check" size={16} />
                    <span>Approve</span>
                  </Button>
                  <Button variant="outline" size="sm">
                    <Icon name="Eye" size={16} />
                    <span>Review</span>
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {/* Detail Modal */}
      {selectedRecord && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={() => setSelectedRecord(null)}>
          <div className="card max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e?.stopPropagation()}>
            <div className="flex items-start justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-foreground mb-1">
                  {selectedRecord?.staff?.full_name}
                </h2>
                <p className="text-muted-foreground">
                  {format(new Date(selectedRecord?.pay_period_start), 'MMM d')} -{' '}
                  {format(new Date(selectedRecord?.pay_period_end), 'MMM d, yyyy')}
                </p>
              </div>
              <button
                onClick={() => setSelectedRecord(null)}
                className="p-2 rounded-lg hover:bg-muted transition-colors"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            <div className="space-y-6">
              {/* Hours Summary */}
              <div>
                <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                  <Icon name="Clock" size={18} className="text-primary" />
                  Hours Summary
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-muted/30 rounded-lg">
                    <div className="text-sm text-muted-foreground mb-1">Regular Hours</div>
                    <div className="text-2xl font-bold text-foreground">{formatHours(selectedRecord?.regular_hours)}</div>
                  </div>
                  <div className="p-4 bg-warning/10 rounded-lg">
                    <div className="text-sm text-muted-foreground mb-1">Overtime Hours</div>
                    <div className="text-2xl font-bold text-warning">{formatHours(selectedRecord?.overtime_hours)}</div>
                  </div>
                </div>
              </div>

              {/* Earnings */}
              <div>
                <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                  <Icon name="TrendingUp" size={18} className="text-success" />
                  Earnings
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between p-2 rounded bg-muted/30">
                    <span className="text-muted-foreground">Regular Pay:</span>
                    <span className="font-medium text-foreground">{formatCurrency(selectedRecord?.regular_pay)}</span>
                  </div>
                  <div className="flex justify-between p-2 rounded bg-muted/30">
                    <span className="text-muted-foreground">Overtime Pay:</span>
                    <span className="font-medium text-foreground">{formatCurrency(selectedRecord?.overtime_pay)}</span>
                  </div>
                  {selectedRecord?.bonus > 0 && (
                    <div className="flex justify-between p-2 rounded bg-success/10">
                      <span className="text-muted-foreground">Bonus:</span>
                      <span className="font-medium text-success">{formatCurrency(selectedRecord?.bonus)}</span>
                    </div>
                  )}
                  {selectedRecord?.commission > 0 && (
                    <div className="flex justify-between p-2 rounded bg-success/10">
                      <span className="text-muted-foreground">Commission:</span>
                      <span className="font-medium text-success">{formatCurrency(selectedRecord?.commission)}</span>
                    </div>
                  )}
                  <div className="flex justify-between p-2 rounded bg-primary/10 font-semibold">
                    <span className="text-foreground">Gross Pay:</span>
                    <span className="text-primary">{formatCurrency(selectedRecord?.gross_pay)}</span>
                  </div>
                </div>
              </div>

              {/* Deductions */}
              <div>
                <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                  <Icon name="TrendingDown" size={18} className="text-error" />
                  Deductions
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between p-2 rounded bg-muted/30">
                    <span className="text-muted-foreground">Tax Deductions:</span>
                    <span className="font-medium text-error">{formatCurrency(selectedRecord?.tax_deductions)}</span>
                  </div>
                  <div className="flex justify-between p-2 rounded bg-muted/30">
                    <span className="text-muted-foreground">Benefit Deductions:</span>
                    <span className="font-medium text-error">{formatCurrency(selectedRecord?.benefit_deductions)}</span>
                  </div>
                  <div className="flex justify-between p-2 rounded bg-muted/30">
                    <span className="text-muted-foreground">Other Deductions:</span>
                    <span className="font-medium text-error">{formatCurrency(selectedRecord?.other_deductions)}</span>
                  </div>
                </div>
              </div>

              {/* Net Pay */}
              <div className="p-6 bg-primary/10 rounded-lg border-2 border-primary">
                <div className="flex items-center justify-between">
                  <span className="text-lg font-semibold text-foreground">Net Pay:</span>
                  <span className="text-3xl font-bold text-primary">{formatCurrency(selectedRecord?.net_pay)}</span>
                </div>
              </div>

              {selectedRecord?.status === 'pending' && (
                <Button onClick={() => handleApprove(selectedRecord?.id)} className="w-full" size="lg">
                  <Icon name="Check" size={20} />
                  <span>Approve Payroll</span>
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PayrollProcessing;